/* eslint-disable import/no-unresolved,@typescript-eslint/ban-ts-comment */
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { toast, Spinner } from "@clarkinc/zest-design-system/ui";
import CustomizableHelpAd from "~/CustomizableProductsPage/CustomizableHelpAd";
import Header from "./Header";
import { User } from "~/common/Store/models/User";
import { sendGtmEvent } from "~/analytics/definitions";
import { addToCartAnalytics } from "~/analytics/AddToCart/addToCartAnalytics";
const AddToCart = React.lazy(() => import("customizer/AddToCart"));
const Details = React.lazy(() => import("customizer/Details"));
const ModelViewer = React.lazy(() => import("customizer/ModelViewer"));
const ProductCustomizer = React.lazy(() => import("customizer/ProductCustomizer"));
const trsTermsOfSaleLink = "https://www.therestaurantstore.com/policies";
const trsTermsOfUseLink = "https://www.therestaurantstore.com/policies#termsOfUse";
export interface CustomizableProductsPageProps {
  isTrs: boolean;
  customizableId: string;
  vendorItemNumber: string;
  productName: string;
  reviews: {
    count: number;
    rating: number;
  };
  itemNumber: string;
  productLink: string;
  freeShipping: boolean;
  freeWebPlusShipping: boolean;
  user: User;
  onLoad?: () => void;
}
const handleCustomizationSummaryClick = () => {
  sendGtmEvent({
    event: "Customization",
    eventAction: "Customization Summary Click"
  });
};
const handleUploadImage = () => {
  sendGtmEvent({
    event: "Customization",
    eventAction: "Upload Image Click"
  });
};
const handleError = (message: string) => {
  sendGtmEvent({
    event: "Customization",
    eventAction: "Customization Error",
    eventLabel: message
  });
};
const onLoadAddToCart = () => {
  const customizationSummaryTrigger = document.getElementById("customization-summary-modal-trigger");
  customizationSummaryTrigger?.addEventListener("click", () => handleCustomizationSummaryClick());
};
export default ({
  isTrs,
  customizableId,
  vendorItemNumber,
  itemNumber,
  productName,
  reviews,
  user,
  productLink,
  freeShipping,
  freeWebPlusShipping
}: CustomizableProductsPageProps) => {
  const [userData, setUserData] = useState(user);
  const getUserData = useCallback(async () => {
    const response = await fetch("/api:edgecache/getdynamicdata");
    const data = await response.json<{
      user: User;
    }>();
    return data.user;
  }, []);
  useEffect(() => {
    if ((!userData?.index || userData?.index === "0") && !userData?.sessionId) {
      getUserData().then(setUserData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const replaceFn = isTrs ? (text: string) => {
    const specialOrdersEmail = "specialorders@webstaurantstore.com";
    const customQuoteEmail = "customquote@webstaurantstore.com";
    const customizableEmail = "customizable@webstaurantstore.com";
    const trsEmail = "TRScustomorders@therestaurantstore.com";
    const replacedContent = text.replace(specialOrdersEmail, trsEmail).replace(customQuoteEmail, trsEmail).replace(customizableEmail, trsEmail);
    const parser = new DOMParser();
    const doc = parser.parseFromString(replacedContent, "text/html");
    const emailLinks = doc.querySelectorAll('a[href^="mailto:"]');
    emailLinks.forEach(email => {
      email.setAttribute("target", "_blank");
      email.setAttribute("rel", "noopener noreferrer");
    });
    return doc.body.innerHTML;
  } : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAddToCart = async (customizationData: any) => {
    let fees = 0;
    Object.entries(customizationData.price.fees).forEach(
    // @ts-ignore

    ([, value]) => fees += value);
    const data = {
      from: "add",
      item_number: customizationData.productDetails.cartItemNumber,
      weight: customizationData.productDetails.weight,
      isHistoryClicked: false,
      price: (customizationData.price.unitPrice * customizationData.quantity + fees) / customizationData.quantity,
      description: `Customized ${customizationData.productDetails.itemNumber.toUpperCase()} - ${customizationData.productDetails.name} (CPI# ${customizationData.id})`,
      uom: customizationData.productDetails.unitOfMeasure,
      qty: customizationData.quantity,
      // shipping: response.shipping,
      pricetoolow: "quote",
      suffix: "",
      ds: "Y",
      group: "N",
      cost: customizationData.productDetails.cost,
      taxcode: "Y",
      category: "0"
    };
    if (isTrs) {
      window.parent.postMessage(JSON.stringify(data), "*");
    } else {
      try {
        const requestBody = JSON.stringify({
          cpid: customizationData.id,
          qty: customizationData.quantity
        });
        const response = await fetch("/myaccount/spa/cart/add/customizable-item", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: requestBody
        });
        if (!response.ok) {
          toast.error("An error occurred, please retry.");
          return;
        }
        addToCartAnalytics({
          source: "customizable product",
          customerIoRunOptions: "only"
        }, {
          itemNumber: customizationData.productDetails.itemNumber,
          quantity: customizationData.quantity
        });
        window.location.href = "/cart";
      } catch (error) {
        toast.error("An error occurred, please retry.");
      }
    }
  };
  const helpAdEmail = isTrs ? "TRScustomorders@therestaurantstore.com" : "customquote@webstaurantstore.com";
  const shippingInfo = (hasFreeShipping: boolean, hasFreeWebPlusShipping: boolean) => {
    if (isTrs || !hasFreeShipping && !hasFreeWebPlusShipping) {
      return <></>;
    }
    return <div className="text-sm pb-2" data-sentry-component="shippingInfo" data-sentry-source-file="index.tsx">
        {hasFreeWebPlusShipping && <a href="/plus/" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-blue-500">
            <span className="mr-1 text-[#3B4238] hover:text-blue-500">
              Ships free with
            </span>
            <span style={{
          textIndent: "-9999px",
          backgroundImage: 'url("//cdnimg.webstaurantstore.com/uploads/design/2017/8/plus_micro.svg")',
          backgroundRepeat: "no-repeat",
          backgroundSize: "35px",
          width: "35px",
          height: "16px",
          display: "inline-block"
        }}>
              Plus
            </span>
          </a>}
        {hasFreeShipping && !hasFreeWebPlusShipping && <div className="ribbon ribbon--mini ribbon--shipping ribbon--shipping-show freeShippingTag" style={{
        display: "inline-block",
        color: "#fff",
        textShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
        transform: "skewX(-15deg) skewY(0)",
        fontSize: "12px",
        fontWeight: "400",
        lineHeight: "13px",
        padding: "2px 7px",
        borderRadius: "4px 1px 4px 1px",
        background: "#006083",
        border: "0"
      }}>
            <span className="ribbon__text" style={{
          display: "block",
          transform: "skewX(15deg) skewY(0)"
        }}>
              Free Shipping
            </span>
          </div>}
      </div>;
  };
  return <main className="container bg-[rgba(238,240,237,1)]">
      {!isTrs && <Header itemNumber={itemNumber} vendorItemNumber={vendorItemNumber} name={productName} reviews={reviews} productLink={productLink} />}
      {userData && <Suspense fallback={<div className="flex justify-center mx-auto pt-14 pb-21 xsl:pt-21 xsl:pb-24 lg:pt-28 lg:pb-[174px]" aria-busy="true">
              <Spinner theme="wss" size="2xlarge" color="dark" label="Loading" labelPosition="stacked" className="justify-self-center" />
            </div>}>
          {/* @ts-ignore */}
          <ProductCustomizer productId={customizableId} userIndex={userData?.index && userData?.index !== "0" ? userData.index : userData.sessionId}>
            <div id="page" className="grid grid-cols-1 lg:grid-cols-[1fr_400px] lg:auto-rows-auto gap-y-5 lg:gap-y-0 gap-x-5">
              <div id="model-viewer" className="lg:col-start-1 lg:col-end-2 lg:row-start-1 lg:row-end-2 col-start-1 col-end-2 row-auto border lg:border-b-0 border-solid border-gray-200 w-full min-w-0 xs:rounded-t xs:rounded-b lg:rounded-b-none">
                <ModelViewer />
              </div>
              <div id="add-to-cart" className="col-start-1 col-end-2 row-auto w-full xs:rounded lg:col-start-2 lg:col-end-3 lg:row-start-1 lg:row-end-4 lg:block"
          // this is to override sf styles which add an additional border
          style={{
            borderTop: 0
          }}>
                <AddToCart
            // @ts-ignore
            links={{
              termsOfUse: isTrs ? trsTermsOfUseLink : "/policies.html#Terms_of_Use",
              termsOfSale: isTrs ? trsTermsOfSaleLink : "/policies.html"
            }} onLoad={onLoadAddToCart} onImageUpload={handleUploadImage} onError={handleError} onAddToCart={handleAddToCart} shippingInfo={() => shippingInfo(freeShipping, freeWebPlusShipping)} />
                {!isTrs && <CustomizableHelpAd className="hidden lg:block mt-5" email={helpAdEmail} />}
              </div>
              <div id="design-considerations" className="lg:col-start-1 lg:col-end-2 lg:row-start-2 lg:row-end-3 col-start-1 col-end-2 row-auto w-full xs:rounded-b xs:rounded-t lg:rounded-t-none" style={{
            borderColor: "#DBDFD8",
            borderStyle: "solid",
            borderWidth: "1px"
          }}>
                <Details
            // @ts-ignore
            replaceFn={replaceFn} contactUsAddr={isTrs ? "TRScustomorders@therestaurantstore.com" : "customorder@webstaurantstore.com"} />
              </div>
              {!isTrs && <CustomizableHelpAd className="lg:hidden w-full" email={helpAdEmail} />}
            </div>
          </ProductCustomizer>
        </Suspense>}
    </main>;
};